/**
 * The single fetch wrapper for the whole app.
 * Responsibilities: base URL, JSON encoding, idempotency keys, timeouts,
 * abort support, and normalising every failure into one Persian-facing shape.
 * No component may call `fetch` directly.
 */
import { config } from '../config';

export type ApiErrorKind =
  | 'network'
  | 'timeout'
  | 'aborted'
  | 'validation'
  | 'unauthorized'
  | 'not_found'
  | 'conflict'
  | 'server'
  | 'unknown';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  /** Persian message safe to render directly. */
  readonly messageFa: string;
  readonly details?: unknown;

  constructor(kind: ApiErrorKind, status: number, messageFa: string, details?: unknown) {
    super(`${kind} (${status}): ${messageFa}`);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.messageFa = messageFa;
    this.details = details;
  }

  /** True when retrying the same request could plausibly succeed. */
  get retryable(): boolean {
    return this.kind === 'network' || this.kind === 'timeout' || this.kind === 'server';
  }
}

const FA_BY_KIND: Record<ApiErrorKind, string> = {
  network: 'اتصال به سرور برقرار نشد. اینترنت را بررسی کنید و دوباره تلاش کنید.',
  timeout: 'پاسخ سرور طول کشید. دوباره تلاش کنید.',
  aborted: 'درخواست لغو شد.',
  validation: 'اطلاعات ارسالی معتبر نیست.',
  unauthorized: 'برای این کار باید دوباره وارد شوید.',
  not_found: 'موردی که خواستید پیدا نشد.',
  conflict: 'این مورد روی سرور تغییر کرده است. صفحه را بازخوانی کنید.',
  server: 'خطایی در سرور رخ داد. کمی بعد دوباره تلاش کنید.',
  unknown: 'خطای ناشناخته‌ای رخ داد.',
};

function kindForStatus(status: number): ApiErrorKind {
  if (status === 401 || status === 403) return 'unauthorized';
  if (status === 404) return 'not_found';
  if (status === 409 || status === 412) return 'conflict';
  if (status === 422 || status === 400) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
}

/** Pulls a Persian message out of the backend's error envelope when present. */
function messageFromBody(body: unknown, fallback: string): string {
  if (!body || typeof body !== 'object') return fallback;
  const record = body as Record<string, unknown>;
  if (typeof record.message_fa === 'string' && record.message_fa) return record.message_fa;
  if (typeof record.detail === 'string' && record.detail) return record.detail;
  const detail = record.detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as Record<string, unknown> | undefined;
    if (first && typeof first.msg === 'string') return first.msg;
  }
  return fallback;
}

export interface RequestOptions {
  /** Explicit integration headers; never include upstream service credentials. */
  headers?: Record<string, string>;
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /** Sends an Idempotency-Key header. Required by order/payment endpoints. */
  idempotencyKey?: string;
  timeoutMs?: number;
  /** Send cookies — the admin session is cookie-based. */
  credentials?: RequestCredentials;
}

const DEFAULT_TIMEOUT = 15_000;

/** Stable, collision-resistant idempotency key (8–100 chars per the spec). */
export function newIdempotencyKey(prefix = 'gs'): string {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().replace(/-/g, '')
      : Math.random().toString(36).slice(2).padEnd(16, '0');
  return `${prefix}-${Date.now().toString(36)}-${random}`.slice(0, 100);
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = config.apiBaseUrl;
  const clean = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(base + clean, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === null || value === undefined || value === '') continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    // Bodyless POSTs stall in WebKit through the production HTTPS proxy.
    // Explicit JSON framing leaves GET, supplied JSON and FormData unchanged.
    body = method === 'POST' ? {} : undefined,
    query,
    signal,
    idempotencyKey,
    timeoutMs = DEFAULT_TIMEOUT,
    credentials = 'include',
  } = options;

  const controller = new AbortController();
  const onAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onAbort, { once: true });
  }
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  let timedOut = false;
  const timeoutWatcher = window.setTimeout(() => {
    timedOut = true;
  }, timeoutMs);

  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };
  const multipart = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body !== undefined && !multipart) headers['Content-Type'] = 'application/json';
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  // Owner-panel changes carry this header; the API refuses them without it (CSRF).
  if (method !== 'GET' && path.startsWith('/admin')) headers['X-Garson-Admin'] = '1';

  try {
    const response = await fetch(buildUrl(path, query), {
      method,
      headers,
      credentials,
      signal: controller.signal,
      ...(body !== undefined ? { body: multipart ? body : JSON.stringify(body) } : {}),
    });

    if (response.status === 204) return undefined as T;

    const text = await response.text();
    let parsed: unknown = null;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text;
      }
    }

    if (response.status === 401 && !path.startsWith('/admin') && !path.startsWith('/demo-auth')) {
      // A demo menu that needs a sign-in: the app routes the visitor to the login page.
      window.dispatchEvent(new Event('garson:login-required'));
    }
    if (!response.ok) {
      const kind = kindForStatus(response.status);
      throw new ApiError(
        kind,
        response.status,
        messageFromBody(parsed, FA_BY_KIND[kind]),
        parsed,
      );
    }

    return parsed as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (error instanceof DOMException && error.name === 'AbortError') {
      if (timedOut) throw new ApiError('timeout', 0, FA_BY_KIND.timeout);
      throw new ApiError('aborted', 0, FA_BY_KIND.aborted);
    }
    throw new ApiError('network', 0, FA_BY_KIND.network, error);
  } finally {
    window.clearTimeout(timer);
    window.clearTimeout(timeoutWatcher);
    if (signal) signal.removeEventListener('abort', onAbort);
  }
}

/** Wraps an unknown thrown value as an ApiError so UI code has one shape. */
export function asApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  if (error instanceof DOMException && error.name === 'AbortError') {
    return new ApiError('aborted', 0, FA_BY_KIND.aborted);
  }
  return new ApiError('unknown', 0, FA_BY_KIND.unknown, error);
}
